import type { GetOccupancyMetricsParams } from '@/types/agentEvents';
import { MetricGrouping, MetricIntervalPeriod } from '@/types/agentEvents';

/** Filter state for metrics (occupancy, adherence, etc.) – reused across dashboard/manager components. */
export interface MetricsFilterParams {
  startTime: string;
  endTime: string;
  groupings: MetricGrouping[];
  intervalPeriod: MetricIntervalPeriod;
  timeZone: string;
}

/** IANA timezone when the runtime cannot resolve a local zone (SSR, older engines). */
export const DEFAULT_METRICS_TIMEZONE = 'America/Los_Angeles';

/** User's IANA timezone from the browser, with {@link DEFAULT_METRICS_TIMEZONE} as fallback. */
export function getDefaultMetricsTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === 'string' && tz.length > 0
      ? tz
      : DEFAULT_METRICS_TIMEZONE;
  } catch {
    return DEFAULT_METRICS_TIMEZONE;
  }
}

/** Default date range: last 7 days, ROUTING_PROFILE grouping. For occupancy and similar. */
export function defaultOccupancyParams(): MetricsFilterParams {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 7);
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    groupings: [MetricGrouping.ROUTING_PROFILE],
    intervalPeriod: MetricIntervalPeriod.TOTAL,
    timeZone: getDefaultMetricsTimezone(),
  };
}

/** Default range for sections that use HOUR interval by default (< 3 days). */
export function defaultHourRange(): Pick<
  MetricsFilterParams,
  'startTime' | 'endTime'
> {
  const end = new Date();
  const start = new Date(end.getTime() - 71 * 60 * 60 * 1000);
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
  };
}

/** Default for adherence and other queue-based metrics: last 7 days, QUEUE grouping. */
export function defaultAdherenceParams(): MetricsFilterParams {
  return defaultQueueMetricsParams();
}

/** Default for contacts-handled, abandonment-rate, average-handle-time, average-queue-answer-time, service-level. */
export function defaultQueueMetricsParams(): MetricsFilterParams {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 7);
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    groupings: [MetricGrouping.QUEUE],
    intervalPeriod: MetricIntervalPeriod.TOTAL,
    timeZone: getDefaultMetricsTimezone(),
  };
}

/** Max allowed range (exclusive): difference between EndTime and StartTime must be less than this many hours. */
export function getMaxRangeHours(period: MetricIntervalPeriod): number {
  // Backend validation rules:
  // HOUR interval: < 72 hours, keep at 71 by default for safety
  // DAY/WEEK intervals: < 35 days, i.e. 35*24 - 1 hours
  // TOTAL interval: Any range (no limit)
  if (period === MetricIntervalPeriod.HOUR) {
    return 71;
  }
  if (period === MetricIntervalPeriod.TOTAL) {
    return Infinity; // No limit
  }
  return 35 * 24 - 1; // DAY, WEEK, FIFTEEN_MIN, THIRTY_MIN
}

/** Validates if date range complies with backend constraints */
export function validateDateRange(
  startTime: string,
  endTime: string,
  period: MetricIntervalPeriod
): { isValid: boolean; message?: string } {
  try {
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();

    if (isNaN(start) || isNaN(end)) {
      return { isValid: false, message: 'Invalid date format' };
    }

    if (start >= end) {
      return { isValid: false, message: 'Start date must be before end date' };
    }

    const maxHours = getMaxRangeHours(period);

    // TOTAL interval has no limit
    if (!isFinite(maxHours)) {
      return { isValid: true };
    }

    const diffInMs = end - start;
    const diffInHours = diffInMs / (60 * 60 * 1000);

    if (diffInHours >= maxHours) {
      return {
        isValid: false,
        message: `Date range must be less than ${maxHours} hours for ${period} interval. Current range: ${diffInHours.toFixed(1)} hours.`,
      };
    }

    return { isValid: true };
  } catch (error) {
    return { isValid: false, message: 'Error validating date range' };
  }
}

/** Clamp endTime so that (endTime - startTime) is less than getMaxRangeHours(period) hours. Keeps startTime fixed. */
export function clampEndToMaxRange(
  startTime: string,
  endTime: string,
  period: MetricIntervalPeriod
): string {
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const maxHours = getMaxRangeHours(period);

  // No limit for TOTAL interval
  if (!isFinite(maxHours)) {
    return endTime;
  }

  const maxEnd = start + maxHours * 60 * 60 * 1000;
  if (end >= maxEnd) {
    const clamped = new Date(maxEnd);
    return clamped.toISOString();
  }
  return endTime;
}

/** Clamp startTime so that (endTime - startTime) is less than getMaxRangeHours(period) hours. Keeps endTime fixed. */
export function clampStartToMaxRange(
  startTime: string,
  endTime: string,
  period: MetricIntervalPeriod
): string {
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const maxHours = getMaxRangeHours(period);

  // No limit for TOTAL interval
  if (!isFinite(maxHours)) {
    return startTime;
  }

  const minStart = end - maxHours * 60 * 60 * 1000;
  if (start < minStart) {
    const clamped = new Date(minStart);
    return clamped.toISOString();
  }
  return startTime;
}

/** Format Date or ISO string for datetime-local input (YYYY-MM-DDTHH:mm). */
export function toDateTimeLocalInput(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toISOString().slice(0, 16);
}

/** Build API query params from filter state. Used by occupancy, adherence, and other metrics endpoints. */
export function toMetricsQueryParams(
  filter: MetricsFilterParams
): GetOccupancyMetricsParams {
  return {
    startTime: filter.startTime,
    endTime: filter.endTime,
    groupings: filter.groupings,
    intervalPeriod: filter.intervalPeriod,
    timeZone: filter.timeZone,
  };
}

/**
 * Validates filter params and returns query params if valid
 * Returns { params, isValid, message? }
 */
export function toValidatedMetricsQueryParams(filter: MetricsFilterParams): {
  params?: GetOccupancyMetricsParams;
  isValid: boolean;
  message?: string;
} {
  // Validate the filter
  const validation = validateDateRange(
    filter.startTime,
    filter.endTime,
    filter.intervalPeriod
  );

  if (!validation.isValid) {
    return {
      isValid: false,
      message: validation.message,
    };
  }

  return {
    params: toMetricsQueryParams(filter),
    isValid: true,
  };
}
